/**
 * Forwards `whatsapp-cta:violation` events to Sentry.
 *
 * The Sentry SDK is treated as optional — if `window.Sentry` is not
 * present (e.g. SDK not yet installed, blocked by an ad-blocker), this
 * forwarder no-ops silently so the watchdog still logs to the console.
 *
 * To enable real reporting:
 *   1. `bun add @sentry/react`
 *   2. Initialise Sentry once at app boot (e.g. in `main.tsx`):
 *        Sentry.init({ dsn: import.meta.env.VITE_SENTRY_DSN });
 *      and assign it to `window.Sentry` (the SDK does this automatically
 *      via `Sentry.onLoad` / browser bundle, or do it manually).
 */

type SentryLike = {
  captureMessage?: (
    message: string,
    captureContext?: {
      level?: "fatal" | "error" | "warning" | "log" | "info" | "debug";
      tags?: Record<string, string>;
      extra?: Record<string, unknown>;
      fingerprint?: string[];
    },
  ) => void;
  withScope?: (cb: (scope: unknown) => void) => void;
};

interface ViolationDetail {
  kind: string;
  detail: Record<string, unknown>;
  url: string;
  ts: number;
}

function getSentry(): SentryLike | undefined {
  return (globalThis as unknown as { Sentry?: SentryLike }).Sentry;
}

let started = false;

function handleViolation(event: Event) {
  const ce = event as CustomEvent<ViolationDetail>;
  const v = ce.detail;
  if (!v) return;

  const sentry = getSentry();
  if (!sentry?.captureMessage) return; // SDK absent → console.error already logged it

  try {
    sentry.captureMessage(`whatsapp-cta violation: ${v.kind}`, {
      level: "error",
      tags: {
        component: "whatsapp-cta",
        violation_kind: v.kind,
        route: v.url,
      },
      extra: {
        kind: v.kind,
        detail: v.detail,
        url: v.url,
        ts: v.ts,
        iso: new Date(v.ts).toISOString(),
        userAgent: navigator.userAgent,
        viewport: { w: window.innerWidth, h: window.innerHeight },
      },
      // Group all violations of the same kind on the same route together.
      fingerprint: ["whatsapp-cta", v.kind, v.url],
    });
  } catch {
    /* never let reporting throw */
  }
}

export function startWhatsAppCtaSentryForwarder(): void {
  if (started || typeof window === "undefined") return;
  started = true;
  window.addEventListener(
    "whatsapp-cta:violation",
    handleViolation as EventListener,
  );
}

export function stopWhatsAppCtaSentryForwarder(): void {
  if (!started) return;
  started = false;
  window.removeEventListener(
    "whatsapp-cta:violation",
    handleViolation as EventListener,
  );
}
